/**
 * Amorçage du thème injecté par frontend/server.js dans le HTML servi
 * (`window.__SHOP_BOOTSTRAP__`) : moteur, identité de marque, jetons
 * (palette, typographie, forme, espacement, contrôles) et sections
 * d'en-tête / pied de page (et de l'accueil sur « / »).
 *
 * Pourquoi : sans lui, le premier rendu affichait le chrome et l'identité
 * par DÉFAUT (en-tête ClickExpert, monogramme, palette d'origine) le temps
 * que /theme/init et /theme/sections répondent, puis tout était remplacé par
 * le thème publié — un flash visible sur chaque moteur de thème. Avec lui,
 * les caches de lib/themeSections.js, context/BrandingContext.jsx et
 * App.js sont remplis AVANT le premier rendu : la page s'ouvre directement
 * dans le bon thème.
 *
 * Jamais utilisé en prévisualisation Studio (brouillon chargé par ses
 * propres paramètres) ni en développement sans server.js (les appels réseau
 * habituels prennent le relais, avec la garde anti-flash de Layout.jsx).
 */
import { isStudioPreview, previewQueryParams } from "@/lib/studioPreviewBridge";
import { getLang } from "@/lib/lang";
import applyPalette from "@/lib/applyPalette";
import applyTypography from "@/lib/applyTypography";
import applyShape from "@/lib/applyShape";
import applySpacing from "@/lib/applySpacing";
import applyControls from "@/lib/applyControls";

export function readBootstrap() {
  if (typeof window === "undefined") return null;
  const boot = window.__SHOP_BOOTSTRAP__;
  if (!boot || typeof boot !== "object") return null;
  if (isStudioPreview() || Object.keys(previewQueryParams()).length > 0) return null;
  return boot;
}

/** Sections amorcées, ou null si la langue du visiteur n'est pas celle de
 * l'amorçage (les sections traduites diffèrent — on laisse le réseau faire). */
export function bootstrapSections() {
  const boot = readBootstrap();
  if (!boot?.sections) return null;
  if ((boot.lang || null) !== (getLang() || null)) return null;
  return boot.sections;
}

export function bootstrapBranding() {
  return readBootstrap()?.branding || null;
}

/** Applique les jetons du thème dès le chargement du bundle (avant le premier
 * rendu React) et renvoie la réponse /theme/init amorcée, ou null. */
export function bootstrapThemeInit() {
  const init = readBootstrap()?.init;
  if (!init || typeof init !== "object") return null;
  try {
    applyPalette(init.palette);
    applyTypography(init.typography);
    applyShape(init.shape);
    applySpacing(init.spacing);
    applyControls(init.controls);
  } catch {
    /* un jeton illisible ne doit pas empêcher la page de s'afficher */
  }
  return init;
}
